// apps/web/src/pages/author/widget-config/dispatcher.tsx
//
// Phase 8 Wave-5 SIBLING of apps/web/src/widgets/template-renderers.ts +
// apps/web/src/widgets/template-renderer.tsx — the per-template CONFIG
// sub-form dispatcher (authr-05). Where the Phase 6/7 dispatcher routes a
// rendered widget by templateId, this dispatcher routes an AUTHORING UI
// editor by templateId.
//
// 4-layer WIDG-10 defense mirrored from template-renderers.ts:
//   Layer 1 (compile-time):   `ReadonlyMap<TemplateId, ConfigSubFormFC>` type
//     erases .set / .delete — see the @ts-expect-error in dispatcher.test.tsx
//     that proves the type-erasure remains in place.
//   Layer 2 (runtime throw):  not applicable (no plugin loader on the author tier).
//   Layer 3 (runtime freeze): `Object.freeze(_configSubForms)` — THIS FILE.
//   Layer 4 (static analysis): the existing scripts/check-registry-closed.sh
//     grep gate catches mutation callsites on the Phase 6/7 renderer Map; the
//     new dispatcher Map is named consistently (`configSubForms`) so a parallel
//     gate could be added in a future plan without changing patterns. Phase 8
//     ships the mirrored shape — Plan 12 may extend the static gate.
//
// shopping-list-aggregator is Phase 11-owned (Phase 01.1 D-28) — absent by
// design. The fallback Alert is the documented unknown-templateId UX
// (defense-in-depth for hand-crafted JSON paste payloads / Plan 11 imports).
//
// Critical correctness note (RESEARCH §"Pitfall 2"): Object.freeze on a Map
// does NOT block .set() / .delete() because Map internals live in slots, not
// own properties. The ReadonlyMap type erasure (Layer 1) blocks at compile
// time; Object.freeze (Layer 3) freezes the OBJECT shell. Belt-and-suspenders.

import type { TemplateId } from "@the-recipe-spread/shared";
import { AlertCircle } from "lucide-react";
import type * as React from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { BranchVariantConfigSubForm } from "./branch-variant-config.js";
import { OptionSetConfigSubForm } from "./option-set-config.js";
import { RankedTierConfigSubForm } from "./ranked-tier-config.js";
import { SizeAndYieldConfigSubForm } from "./size-and-yield-config.js";
import { StaticNoteConfigSubForm } from "./static-note-config.js";
import { SummaryChecklistConfigSubForm } from "./summary-checklist-config.js";

export interface ConfigSubFormProps {
  instanceId: string;
  basePath: string;
}

export type ConfigSubFormFC = React.FC<ConfigSubFormProps>;

// All six per-template config sub-forms; "shopping-list-aggregator" is
// owned by Phase 11 (Phase 01.1 D-28 catalog ownership table) — absent by
// design. Plan 11's eventual shopping-list-aggregator-config.tsx will add the
// seventh entry; until then, the dispatcher's unknown-templateId branch
// returns the documented fallback Alert (T-08-09-02 mitigation).
const _configSubForms = new Map<TemplateId, ConfigSubFormFC>([
  ["option-set", OptionSetConfigSubForm as ConfigSubFormFC],
  ["summary-checklist", SummaryChecklistConfigSubForm as ConfigSubFormFC],
  ["static-note", StaticNoteConfigSubForm as ConfigSubFormFC],
  ["size-and-yield", SizeAndYieldConfigSubForm as ConfigSubFormFC],
  ["ranked-tier", RankedTierConfigSubForm as ConfigSubFormFC],
  ["branch-variant", BranchVariantConfigSubForm as ConfigSubFormFC],
]);

// Layer 3: freeze the Map shell.
export const configSubForms: ReadonlyMap<TemplateId, ConfigSubFormFC> =
  Object.freeze(_configSubForms);

export interface WidgetConfigSubFormProps {
  templateId: string;
  instanceId: string;
  basePath: string;
}

// Dispatcher — routes by templateId to the matching ConfigSubForm. Unknown
// templateId (including shopping-list-aggregator and any hand-crafted paste
// payload that carries a templateId outside the closed registry) renders the
// documented destructive Alert per T-08-09-01.
export function WidgetConfigSubForm({
  templateId,
  instanceId,
  basePath,
}: WidgetConfigSubFormProps): React.ReactElement {
  const SubForm = configSubForms.get(templateId as TemplateId);
  if (SubForm === undefined) {
    return (
      <Alert variant="destructive">
        <AlertCircle size={16} />
        <AlertTitle>Widget config unavailable</AlertTitle>
        <AlertDescription>
          Configuration UI not available for this widget template.
        </AlertDescription>
      </Alert>
    );
  }
  return <SubForm instanceId={instanceId} basePath={basePath} />;
}
WidgetConfigSubForm.displayName = "WidgetConfigSubForm";
